import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { landingPathForRole } from "@/lib/constants";
import ProtectedRoute from "@/components/ProtectedRoute";
import ThemeProvider from "@/components/ThemeProvider";
import Layout from "@/components/Layout";

// Login stays eager — it's the first paint for signed-out users.
import Login from "@/pages/Login";

// Every other page is code-split: users only download the routes they visit.
// Heavy deps (recharts on Dashboard, exports on data pages) stay out of the
// initial bundle, which matters most for sales users scoped to the finder.
const Signup = lazy(() => import("@/pages/Signup"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Policies = lazy(() => import("@/pages/Policies"));
const Companies = lazy(() => import("@/pages/Companies"));
const Products = lazy(() => import("@/pages/Products"));
const Agents = lazy(() => import("@/pages/Agents"));
const PayoutGrid = lazy(() => import("@/pages/PayoutGrid"));
const Commissions = lazy(() => import("@/pages/Commissions"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const CompanyPayouts = lazy(() => import("@/pages/CompanyPayouts"));
const BankUpload = lazy(() => import("@/pages/BankUpload"));
const AuditLogs = lazy(() => import("@/pages/AuditLogs"));
const Ledger = lazy(() => import("@/pages/Ledger"));
const Renewals = lazy(() => import("@/pages/Renewals"));
const Organization = lazy(() => import("@/pages/Organization"));
const CommissionGrids = lazy(() => import("@/pages/CommissionGrids"));
const GridExplorer = lazy(() => import("@/pages/GridExplorer"));
const ImageScans = lazy(() => import("@/pages/ImageScans"));
const Scorecard = lazy(() => import("@/pages/Scorecard"));
const ColumnMaps = lazy(() => import("@/pages/ColumnMaps"));
const QuickGridFinder = lazy(() => import("@/pages/QuickGridFinder"));
const RateCards = lazy(() => import("@/pages/RateCards"));
const PromptManager = lazy(() => import("@/pages/PromptManager"));
const Reconciliation = lazy(() => import("@/pages/Reconciliation"));
const ReconcileForm = lazy(() => import("@/pages/ReconcileForm"));
const Settings = lazy(() => import("@/pages/Settings"));
const Superadmin = lazy(() => import("@/pages/Superadmin"));


// Index / fallback landing — role-aware so sales users (scoped to the finder)
// don't land on a page they can't access.
function RoleLanding() {
  const { user } = useAuth();
  const to = user && user !== false ? landingPathForRole(user.role) : "/dashboard";
  return <Navigate to={to} replace />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          {/* Top-level boundary only catches routes outside the app shell
              (Signup); in-shell pages use the PageFallback inside Layout. */}
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<RoleLanding />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute roles={["admin", "agent", "accountant"]}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/policies"
                element={
                  <ProtectedRoute roles={["admin", "agent", "accountant"]}>
                    <Policies />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <Companies />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <Products />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <Agents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payout-grid"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <PayoutGrid />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/commissions"
                element={
                  <ProtectedRoute roles={["admin", "agent", "accountant"]}>
                    <Commissions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <Invoices />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/company-payouts"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <CompanyPayouts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bank"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <BankUpload />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <AuditLogs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ledger"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <Ledger />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/renewals"
                element={
                  <ProtectedRoute roles={["admin", "agent", "accountant"]}>
                    <Renewals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organization"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Organization />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/commission-grids"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <CommissionGrids />
                  </ProtectedRoute>
                }
              />
              {["/column-maps", "/column-maps/:id"].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute roles={["admin", "accountant"]}>
                      <ColumnMaps />
                    </ProtectedRoute>
                  }
                />
              ))}
              <Route
                path="/scorecard"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Scorecard />
                  </ProtectedRoute>
                }
              />
              {["/image-scans", "/image-scans/:id"].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute roles={["admin", "accountant"]}>
                      <ImageScans />
                    </ProtectedRoute>
                  }
                />
              ))}
              <Route
                path="/grid-explorer"
                element={
                  <ProtectedRoute roles={["admin", "agent", "accountant"]}>
                    <GridExplorer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rate-cards"
                element={
                  <ProtectedRoute roles={["admin", "agent", "accountant"]}>
                    <RateCards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/grid-finder"
                element={
                  <ProtectedRoute roles={["admin", "agent", "accountant", "sales"]}>
                    <QuickGridFinder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prompts"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <PromptManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reconciliation"
                element={
                  <ProtectedRoute roles={["admin", "accountant"]}>
                    <Reconciliation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reconcile-check"
                element={
                  <ProtectedRoute roles={["admin", "accountant", "sales"]}>
                    <ReconcileForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute roles={["admin", "agent", "accountant", "sales"]}>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin"
                element={
                  <ProtectedRoute roles={["superadmin"]}>
                    <Superadmin />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="*" element={<RoleLanding />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
